import React from 'react';
import { motion } from 'framer-motion';
import ScrollHeader from '../components/ScrollHeader';
import ImageModal from '../components/ImageModal';

const Projects: React.FC = () => {
  const [selectedImage, setSelectedImage] = React.useState<string | null>(null);

  return (
    <div className="max-w-4xl mx-auto">
      <ScrollHeader title="Projects" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="prose prose-lg dark:prose-invert max-w-none">
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            Organize your infrastructure work into projects. Projects are the foundation for using Ana's features.
          </p>

          <div className="bg-primary-500/10 dark:bg-primary-500/20 border-l-4 border-primary-500 p-6 rounded-r-lg mb-8">
            <h3 className="text-primary-600 dark:text-primary-400 mt-0 mb-2">💡 Key Concept</h3>
            <p className="mb-0">
              Projects are containers for your infrastructure work. You must create or select a project 
              before you can use the Chat feature to generate infrastructure code. All generated code, 
              configurations, and deployments are organized within projects.
            </p>
          </div>

          <h2>Overview</h2>
          <p>
            The Projects page is where you manage all your infrastructure projects. Each project represents 
            a distinct infrastructure setup, application, or environment that you're working on with Ana.
          </p>

          <div 
            className="my-8 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-700 cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors"
            onClick={() => setSelectedImage('/screenshots/projects/04_projects.png')}
          >
            <img 
              src="/screenshots/projects/04_projects.png" 
              alt="Projects Overview" 
              className="w-full"
            />
          </div>

          <h2>Creating a New Project</h2>
          <p>
            Before you can use Ana's Chat feature, you need to create a project:
          </p>

          <div 
            className="my-8 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-700 cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors"
            onClick={() => setSelectedImage('/screenshots/projects/creat project.png')}
          >
            <img 
              src="/screenshots/projects/creat project.png" 
              alt="Create New Project" 
              className="w-full"
            />
          </div>

          <h3>Project Creation Steps</h3>
          <ol>
            <li>
              <strong>Click "Create Project":</strong> Navigate to the Projects page and click the create button
            </li>
            <li>
              <strong>Enter Project Details:</strong>
              <ul>
                <li>Project name (e.g., "Production Infrastructure", "Dev Environment")</li>
                <li>Description (optional but recommended)</li>
                <li>Tags for organization (optional)</li>
              </ul>
            </li>
            <li>
              <strong>Configure Settings:</strong>
              <ul>
                <li>Default cloud provider (AWS, Azure, GCP)</li>
                <li>Default region</li>
                <li>Team members (if applicable)</li>
              </ul>
            </li>
            <li>
              <strong>Create Project:</strong> Click create to initialize the project
            </li>
          </ol>

          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-6 rounded-r-lg mb-6">
            <h4 className="text-blue-800 dark:text-blue-400 mt-0 mb-2">📁 Project Organization</h4>
            <p className="mb-0 text-blue-700 dark:text-blue-300">
              Organize projects by environment (dev, staging, production), application, or team. 
              Good project organization makes it easier to manage infrastructure and collaborate with team members.
            </p>
          </div>

          <h2>Project Management</h2>

          <h3>Selecting a Project</h3>
          <p>
            To work with a project:
          </p>
          <ul>
            <li>Navigate to the Projects page</li>
            <li>Click on the project card you want to work with</li>
            <li>The project becomes active for all subsequent operations</li>
            <li>Navigate to Chat to start generating infrastructure code</li>
          </ul>

          <h3>Project Information</h3>
          <p>
            Each project displays:
          </p>
          <ul>
            <li><strong>Project Name:</strong> Unique identifier for the project</li>
            <li><strong>Description:</strong> Brief overview of the project's purpose</li>
            <li><strong>Created Date:</strong> When the project was created</li>
            <li><strong>Last Modified:</strong> Most recent activity timestamp</li>
            <li><strong>Resource Count:</strong> Number of infrastructure resources</li>
            <li><strong>Status:</strong> Active, archived, or in development</li>
          </ul>

          <h3>Project Actions</h3>
          <p>
            Available actions for each project:
          </p>
          <ul>
            <li><strong>View Code:</strong> Open the Code Editor to see project files</li>
            <li><strong>Visualize:</strong> Open the Visualisation tool for the project</li>
            <li><strong>Edit Settings:</strong> Modify project configuration</li>
            <li><strong>Archive:</strong> Archive completed or inactive projects</li>
            <li><strong>Delete:</strong> Permanently remove a project (with confirmation)</li>
            <li><strong>Duplicate:</strong> Create a copy of the project as a starting point</li>
          </ul>

          <h2>Project Workflow</h2>

          <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg mb-6">
            <h3 className="text-gray-700 dark:text-gray-300 mt-0 mb-4">Typical Project Lifecycle:</h3>
            <ol className="space-y-3 mb-0">
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Create Project:</strong> Set up a new project with basic information
              </li>
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Generate Infrastructure:</strong> Use Chat to describe and generate infrastructure code
              </li>
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Review Code:</strong> Examine generated code in the Code Editor
              </li>
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Visualize:</strong> View infrastructure architecture in the Visualisation tool
              </li>
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Configure Integration:</strong> Set up cloud provider credentials
              </li>
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Deploy:</strong> Deploy infrastructure to the cloud
              </li>
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Monitor & Optimize:</strong> Use Cloud Analyzer for ongoing optimization
              </li>
            </ol>
          </div>

          <h2>Best Practices</h2>

          <h3>Project Naming</h3>
          <p>
            Use clear, descriptive names for your projects:
          </p>
          <ul>
            <li>Include environment (e.g., "Production Web App", "Dev Database")</li>
            <li>Use consistent naming conventions across projects</li>
            <li>Avoid generic names like "Project 1" or "Test"</li>
            <li>Consider including the application or service name</li>
          </ul>

          <h3>Project Organization</h3>
          <p>
            Organize projects effectively:
          </p>
          <ul>
            <li>Separate projects by environment (development, staging, production)</li>
            <li>Group related infrastructure in the same project</li>
            <li>Use tags to categorize projects by team, application, or purpose</li>
            <li>Archive old or completed projects to keep the list manageable</li>
          </ul>

          <h3>Collaboration</h3>
          <p>
            When working with teams:
          </p>
          <ul>
            <li>Add team members to projects they need access to</li>
            <li>Use clear descriptions to help team members understand project purpose</li>
            <li>Document any special configurations or requirements</li>
            <li>Establish naming conventions for consistency</li>
          </ul>

          <h2>Project Templates</h2>
          <p>
            Ana provides project templates for common use cases:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-light-surface dark:bg-dark-surface p-4 rounded-lg border border-light-border dark:border-dark-border">
              <h4 className="text-primary-500 dark:text-primary-400 mt-0 mb-2">Web Application</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-0">
                Complete infrastructure for web applications including compute, database, and CDN
              </p>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-4 rounded-lg border border-light-border dark:border-dark-border">
              <h4 className="text-primary-500 dark:text-primary-400 mt-0 mb-2">Microservices</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-0">
                Kubernetes cluster with service mesh, monitoring, and CI/CD
              </p>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-4 rounded-lg border border-light-border dark:border-dark-border">
              <h4 className="text-primary-500 dark:text-primary-400 mt-0 mb-2">Data Pipeline</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-0">
                ETL infrastructure with data lakes, processing, and analytics
              </p>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-4 rounded-lg border border-light-border dark:border-dark-border">
              <h4 className="text-primary-500 dark:text-primary-400 mt-0 mb-2">Serverless API</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-0">
                API Gateway, Lambda functions, and managed databases
              </p>
            </div>
          </div>

          <h2>Next Steps</h2>
          <p>
            After creating a project:
          </p>
          <ol>
            <li>
              <strong>Start Chatting:</strong> Navigate to Chat and describe your infrastructure needs
            </li>
            <li>
              <strong>Review Generated Code:</strong> Check the Code Editor to see what Ana created
            </li>
            <li>
              <strong>Visualize Infrastructure:</strong> Use the eye button to see the visual diagram
            </li>
            <li>
              <strong>Set up Integration:</strong> Configure cloud provider credentials for deployment
            </li>
          </ol>

          <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-6 rounded-r-lg mt-8">
            <h3 className="text-green-800 dark:text-green-400 mt-0 mb-2">✨ Pro Tip</h3>
            <p className="mb-0 text-green-700 dark:text-green-300">
              Create separate projects for different environments (dev, staging, production) even if they 
              have similar infrastructure. This makes it easier to manage deployments and prevents 
              accidental changes to production environments.
            </p>
          </div>
        </div>
      </motion.div>

      <ImageModal
        isOpen={selectedImage !== null}
        src={selectedImage || ''}
        onClose={() => setSelectedImage(null)}
        alt="Screenshot"

      />
    </div>
  );
};

export default Projects;
